import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Divider,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import axios from 'axios';
import { ProductAttribute } from '../../types/product';

const ProductProperties: React.FC = () => {
  const [attributes, setAttributes] = useState<ProductAttribute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newAttributeName, setNewAttributeName] = useState('');
  const [newAttributeValue, setNewAttributeValue] = useState('');
  const [attributeNewValues, setAttributeNewValues] = useState<{[key: number]: string}>({});

  // Fetch all product attributes
  useEffect(() => {
    fetchAttributes();
  }, []);

  const fetchAttributes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/product-attributes');
      setAttributes(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching product attributes:', err);
      setError('فشل في تحميل خصائص المنتجات');
    } finally {
      setLoading(false);
    }
  };

  // Add a new attribute
  const handleAddAttribute = async () => {
    if (!newAttributeName.trim()) {
      setError('يرجى إدخال اسم الخاصية');
      return;
    }

    // Check if attribute already exists
    if (attributes.some(attr => attr.name === newAttributeName)) {
      setError('هذه الخاصية موجودة بالفعل');
      return;
    }

    const newAttr: ProductAttribute = {
      name: newAttributeName,
      values: newAttributeValue ? [newAttributeValue] : []
    };

    setLoading(true);
    try {
      const response = await axios.post('/api/product-attributes', newAttr);
      setAttributes([...attributes, response.data]);
      setNewAttributeName('');
      setNewAttributeValue('');
      setError(null);
      setSuccess('تمت إضافة الخاصية بنجاح');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error adding attribute:', err);
      setError('فشل في إضافة الخاصية');
    } finally {
      setLoading(false);
    }
  };

  // Add a new value to an attribute
  const handleAddAttributeValue = async (attrIndex: number, value: string | undefined) => {
    if (!value || !value.trim()) {
      setError('يرجى إدخال قيمة صالحة');
      return;
    }

    const attribute = attributes[attrIndex];
    
    // Ensure attribute exists
    if (!attribute || !attribute._id) {
      setError('خطأ: الخاصية غير موجودة');
      console.error('Attribute not found at index:', attrIndex, 'Attributes:', attributes);
      return;
    }

    const trimmedValue = value.trim();

    // Check if value already exists
    if (attribute.values.includes(trimmedValue)) {
      setError('هذه القيمة موجودة بالفعل لهذه الخاصية');
      return;
    }

    const updatedAttribute = {
      ...attribute,
      values: [...attribute.values, trimmedValue]
    };

    setLoading(true);
    try {
      console.log('Sending PUT request to update attribute:', updatedAttribute);
      const response = await axios.put(`/api/product-attributes/${attribute._id}`, updatedAttribute);
      console.log('Server response:', response.data);
      
      // Use the response data from the server to update our state
      const updatedAttributes = [...attributes];
      updatedAttributes[attrIndex] = response.data;
      setAttributes(updatedAttributes);
      
      setError(null);
      setSuccess('تمت إضافة القيمة بنجاح');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error adding attribute value:', err);
      if (axios.isAxiosError(err) && err.response) {
        setError(`فشل في إضافة قيمة الخاصية: ${err.response.data.message || err.message}`);
      } else {
        setError('فشل في إضافة قيمة الخاصية');
      }
    } finally {
      setLoading(false);
    }
  };

  // Remove a value from an attribute
  const handleRemoveAttributeValue = async (attrIndex: number, valueIndex: number) => {
    const attribute = attributes[attrIndex];
    const updatedValues = [...attribute.values];
    updatedValues.splice(valueIndex, 1);

    const updatedAttribute = {
      ...attribute,
      values: updatedValues
    };

    setLoading(true);
    try {
      const response = await axios.put(`/api/product-attributes/${attribute._id}`, updatedAttribute);
      
      // Use the response data from the server to update our state
      const updatedAttributes = [...attributes];
      updatedAttributes[attrIndex] = response.data;
      setAttributes(updatedAttributes);
      
      setError(null);
      setSuccess('تمت إزالة القيمة بنجاح');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing attribute value:', err);
      setError('فشل في إزالة قيمة الخاصية');
    } finally {
      setLoading(false);
    }
  };

  // Remove an attribute
  const handleRemoveAttribute = async (index: number) => {
    const attribute = attributes[index];
    
    setLoading(true);
    try {
      await axios.delete(`/api/product-attributes/${attribute._id}`);
      
      setAttributes(attributes.filter((_, i) => i !== index));
      setError(null);
      setSuccess('تمت إزالة الخاصية بنجاح');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing attribute:', err);
      setError('فشل في إزالة الخاصية');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, direction: 'rtl', textAlign: 'left' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 3, textAlign: 'left' }}>
          إدارة خصائص المنتجات
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
            إضافة خاصية جديدة
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="اسم الخاصية"
                placeholder="مثال: اللون، المقاس، المادة"
                value={newAttributeName}
                onChange={(e) => setNewAttributeName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="قيمة (اختياري)"
                placeholder="مثال: أحمر، كبير، قطن"
                value={newAttributeValue}
                onChange={(e) => setNewAttributeValue(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddAttribute}
                disabled={loading}
              >
                إضافة
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
          قائمة الخصائص
        </Typography>

        {attributes.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'left', py: 2 }}>
            لا توجد خصائص بعد. أضف خصائص لتحديد متغيرات المنتجات.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>اسم الخاصية</TableCell>
                  <TableCell>القيم</TableCell>
                  <TableCell>إضافة قيمة</TableCell>
                  <TableCell>إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attributes.map((attribute, attrIndex) => (
                  <TableRow key={attrIndex}>
                    <TableCell>
                      <Typography variant="subtitle1">
                        {attribute.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {attribute.values.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            لا توجد قيم بعد
                          </Typography>
                        ) : (
                          attribute.values.map((value, valueIndex) => (
                            <Chip
                              key={valueIndex}
                              label={value}
                              onDelete={() => handleRemoveAttributeValue(attrIndex, valueIndex)}
                              size="small"
                            />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          label="قيمة جديدة"
                          placeholder="أضف قيمة جديدة"
                          value={attributeNewValues[attrIndex] || ''}
                          onChange={(e) => {
                            const newValues = { ...attributeNewValues };
                            newValues[attrIndex] = e.target.value;
                            setAttributeNewValues(newValues);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const value = attributeNewValues[attrIndex];
                              if (value && value.trim()) {
                                handleAddAttributeValue(attrIndex, value);
                                // Clear the input after adding
                                const newValues = { ...attributeNewValues };
                                newValues[attrIndex] = '';
                                setAttributeNewValues(newValues);
                              }
                            }
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const value = attributeNewValues[attrIndex];
                            if (value && value.trim()) {
                              handleAddAttributeValue(attrIndex, value);
                              // Clear the input after adding
                              const newValues = { ...attributeNewValues };
                              newValues[attrIndex] = '';
                              setAttributeNewValues(newValues);
                            }
                          }}
                          disabled={loading}
                        >
                          إضافة
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveAttribute(attrIndex)}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default ProductProperties;